import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  WindowScroller,
} from 'react-virtualized';

import { Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import {
  applicationAssetApi,
  applicationBorrowerApi,
  applicationIncomeApi,
  applicationLiabilityApi,
  applicationPropertyApi,
} from 'app/services/api';
import { ViewExtractionField } from 'app/shared/components/document/ExtractionField';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import EditPenIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/EditPenIcon';
import EditExtractionFieldsResultsForm from 'app/shared/modules/LoanApplicationDocuments/DocumentExtractionFieldsButton/EditExtractionFieldsResultsForm';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiDataRecognitionResult } from 'constants/documents';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  getAiExtractionResultAction,
  getDocumentAssociationsAction,
  getDocumentExtractionFilledFieldsAction,
  rerunDocumentExtractionAction,
} from 'store/documents/actions';
import {
  getAiExtractionModelsGroupedByLayers,
  getDocumentExtractionFields,
  selectDocumentAssociationIds,
  selectDocumentFilledExtractionFields,
  selectIsGenAiDocument,
} from 'store/documents/selectors';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import {
  getAssetsAction,
  getBorrowersTableDataAction,
  getFinancialInfoPropertiesAction,
  getIncomesAction,
  getLiabilitiesAction,
} from 'store/loanApplications/actions';
import { selectDocumentAssociationsMulti } from 'store/loanApplications/selectors';
import { getDocumentTypeFieldsMappingByDocumentTypeIdAction } from 'store/system/actions';
import {
  getDocumentFieldTypes,
  selectDocumentExtractionMappingList,
  selectInspectorExtractionFieldsByDocTypeId,
} from 'store/system/selectors';

import { getExtractionFieldsTree } from '../../DocumentExtractionFieldsButton/DocumentExtractionFieldsButton.utils';
import {
  confidenceBadgeHighStyles,
  confidenceBadgeLowStyles,
  confidenceBadgeRowStyles,
} from '../../documentsConfidence.styles';
import { EXTRACTION_PANEL_STATUS } from '../DocumentInspector.constants';
import {
  checkExtractionResultsShape,
  getExtractionFieldLeaves,
  getMissingExtractionFieldLeaves,
} from '../DocumentInspector.utils';

import {
  EDIT_BUTTON_LABEL,
  EXTRACTION_RESULTS_SHAPE_ERROR_MESSAGE,
  EXTRACTION_SECTION_LABEL,
  EXTRACTION_SKELETON_CARD_COUNT,
  EXTRACTION_STATUS_POLL_INTERVAL_MS,
  RERUN_BUTTON_LABEL,
  RERUN_DISABLED_PROCESSING_TOOLTIP,
  SORT_LOW_CONFIDENCE_FIRST_LABEL,
} from './DocumentExtractionFieldsTab.constants';
import {
  aiFieldCardNeedsReviewStyles,
  aiFieldCardStyles,
  correctButtonStyles,
  editAllButtonStyles,
  editFormWrapperStyles,
  emptyFieldsBoxStyles,
  emptyFieldValueStyles,
  footerButtonStyles,
  footerButtonWrapStyles,
  footerStyles,
  frameLabelStyles,
  getFrameSegmentStyles,
  getSortToggleButtonStyles,
  getVirtualizedRowStyles,
  headerActionsStyles,
  miniFieldRowStyles,
  notFoundAccordionStyles,
  reviewActionsRowStyles,
  sectionHeaderStyles,
  skeletonCardStyles,
  virtualizedListStyles,
} from './DocumentExtractionFieldsTab.styles';
import { DocumentExtractionFieldsTabTestIds } from './DocumentExtractionFieldsTab.testIds';
import {
  buildDropdownOptionsMap,
  buildExtractionFieldTypeMap,
  buildExtractionResultsInitialForm,
  buildFieldApplyMap,
  buildNestedExtractionFields,
  checkIsLowConfidence,
  flattenExtractionFieldDefinitions,
  findScrollParent,
  flattenFieldTreeRows,
  getConfidencePillText,
  sortFieldsLowConfidenceFirst,
} from './DocumentExtractionFieldsTab.utils';

const EMPTY_EXTRACTION_INITIAL_FORM = {};

const VIRTUALIZED_ROW_DEFAULT_HEIGHT = 92;
const VIRTUALIZED_OVERSCAN_ROW_COUNT = 6;

const SKELETON_CARD_KEYS = Array.from(
  { length: EXTRACTION_SKELETON_CARD_COUNT },
  (__, index) => `extraction-skeleton-${index}`,
);

const DocumentExtractionFieldsTab = ({
  documentId,
  documentTypeId = '',
  applicationId: applicationIdFromProp = null,
  panelStatus = null,
  onRefreshExtractionRequest = null,
  onShowFieldSource = null,
}) => {
  const dispatch = useDispatch();
  const { applicationId: applicationIdFromUrl } = useParams();
  const applicationId = applicationIdFromProp ?? applicationIdFromUrl ?? null;

  const documentFieldTypes = useSelector(getDocumentFieldTypes, _.isEqual);
  const extractionFields = useSelector(getDocumentExtractionFields(documentId), _.isEqual);
  const extractionFieldsResults = useSelector(selectDocumentFilledExtractionFields, _.isEqual);
  const documentAssociationIds = useSelector(selectDocumentAssociationIds, _.isEqual);
  const documentExtractionMappingList = useSelector(selectDocumentExtractionMappingList);
  const { documentAssociationsOptions } = useSelector(selectDocumentAssociationsMulti);
  const inspectorFlatFields = useSelector(
    selectInspectorExtractionFieldsByDocTypeId(documentTypeId),
    _.isEqual,
  );
  const aiModelsByLayer = useSelector(getAiExtractionModelsGroupedByLayers(documentId), _.isEqual);
  const isGenAiDocument = useSelector(selectIsGenAiDocument(documentId));
  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_EXTRACTION_FIELDS),
  );

  const [isError, setIsError] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [isLowConfidenceFirst, setIsLowConfidenceFirst] = useState(false);
  const [scrollElement, setScrollElement] = useState(null);

  // Tracks which documentId the loaded results belong to so we never render a
  // prior document's extracted fields while the new one loads (PII leak guard).
  // Kept in state (not a ref) so the derived field tree recomputes when it lands.
  const [loadedDocumentId, setLoadedDocumentId] = useState(null);

  const editFormRef = useRef(null);
  const virtualizedListWrapperRef = useRef(null);
  const virtualizedListRef = useRef(null);

  const prevPanelStatusRef = useRef(panelStatus);

  // Tracks which documentIds we've already requested the AI-analysis result for, so the
  // fetch-if-missing effect below fires at most once per document (never a re-request loop).
  const requestedAiResultRef = useRef({});
  // Whether the mapped AI layer was present on the previous render — used to detect a mid-view
  // wipe (a documents-list refresh full-replaces the store and drops document.aiAnalysisResult)
  // and re-arm the fetch so the locate controls self-heal instead of a single re-fetch per doc.
  const hadAiModelsRef = useRef(false);

  const loadExtractionResults = useCallback(() => {
    setIsError(false);
    setLoadedDocumentId(null);
    setIsEditMode(false);

    dispatch(
      getDocumentExtractionFilledFieldsAction({
        documentId,
        silent: true,
      }),
    )
      .then(data => {
        // A results payload we cannot key by documentExtractionFieldId must surface
        // the error banner — not silently render an empty tree.
        if (!checkExtractionResultsShape(data)) {
          setIsError(true);
          dispatch(showErrorMessageAction({ message: EXTRACTION_RESULTS_SHAPE_ERROR_MESSAGE }));

          return;
        }

        setLoadedDocumentId(documentId);
      })
      .catch(err => {
        logger.error(err);
        setIsError(true);
        dispatch(
          showErrorMessageAction({
            message: getDefaultErrorMessage(err),
            err,
          }),
        );
      });
  }, [dispatch, documentId]);

  // The inspector fetches the extraction request when the document opens, so only explicit
  // reloads refresh it here.
  const refreshExtractionRequestAndResults = useCallback(() => {
    onRefreshExtractionRequest?.();
    loadExtractionResults();
  }, [onRefreshExtractionRequest, loadExtractionResults]);

  useEffectSafeStrictMode(() => {
    if (_.isEmpty(documentFieldTypes)) {
      return;
    }

    loadExtractionResults();
  }, [documentFieldTypes, loadExtractionResults]);

  // The locate controls need the AI-analysis result (via aiModelsByLayer). That fetch is owned by
  // the sibling DocumentView on its own timing, so on document re-selection the mapped layer can be
  // transiently empty and every locate control silently disappears (DEV-5156 QA TC-24). Request it
  // here too — once per documentId — so this tab's controls don't depend on the viewer's timing.
  useEffectSafeStrictMode(() => {
    const isLayerPresent = !_.isNil(aiModelsByLayer);

    // A documents-list refresh (upload / tree move-delete-rename / intake) full-replaces the store's
    // documents map and drops the current doc's aiAnalysisResult (DEV-5178 follow-up). When the layer
    // disappears after having been present, re-arm this doc's guard so the fetch below re-runs and the
    // locate controls recover, rather than staying blank until the next document switch.
    if (!isLayerPresent && hadAiModelsRef.current) {
      delete requestedAiResultRef.current[documentId];
    }

    hadAiModelsRef.current = isLayerPresent;

    if (
      !documentId ||
      !isGenAiDocument ||
      isLayerPresent ||
      requestedAiResultRef.current[documentId]
    ) {
      return;
    }

    requestedAiResultRef.current[documentId] = true;
    dispatch(getAiExtractionResultAction({ documentId }));
  }, [dispatch, documentId, isGenAiDocument, aiModelsByLayer]);

  // While extraction runs, poll the document so the chip flips (and the results
  // reload) as soon as the backend finishes without requiring a manual refresh.
  useEffect(() => {
    if (panelStatus !== EXTRACTION_PANEL_STATUS.PROCESSING || !onRefreshExtractionRequest) {
      return;
    }

    const intervalId = setInterval(onRefreshExtractionRequest, EXTRACTION_STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [panelStatus, onRefreshExtractionRequest]);

  useEffectSafeStrictMode(() => {
    const wasProcessing = prevPanelStatusRef.current === EXTRACTION_PANEL_STATUS.PROCESSING;

    prevPanelStatusRef.current = panelStatus;

    if (wasProcessing && panelStatus !== EXTRACTION_PANEL_STATUS.PROCESSING) {
      refreshExtractionRequestAndResults();
    }
  }, [panelStatus, refreshExtractionRequestAndResults]);

  useEffectSafeStrictMode(() => {
    if (!documentTypeId || documentExtractionMappingList?.[documentTypeId]) {
      return;
    }

    dispatch(getDocumentTypeFieldsMappingByDocumentTypeIdAction({ documentTypeId })).catch(err => {
      logger.error(err);
    });
  }, [dispatch, documentTypeId, documentExtractionMappingList]);

  // Load the document's entity associations + the application's entity option lists so the
  // apply-to-entity affordance can resolve (buildFieldApplyMap needs the linked-entity ids and
  // the option metadata, e.g. an income's owning borrowerId). The Links tab loads these on its
  // own; the AI Fields tab must load them too so the affordance renders without first opening Links.
  useEffectSafeStrictMode(() => {
    if (!documentId) {
      return;
    }

    dispatch(getDocumentAssociationsAction({ documentId, silent: true })).catch(err => {
      logger.error(err);
    });
  }, [dispatch, documentId]);

  useEffectSafeStrictMode(() => {
    if (!applicationId) {
      return;
    }

    Promise.all([
      dispatch(getBorrowersTableDataAction({ applicationId, silent: true })),
      dispatch(getIncomesAction({ applicationId, silent: true })),
      dispatch(getAssetsAction({ applicationId, silent: true })),
      dispatch(getFinancialInfoPropertiesAction({ applicationId, silent: true })),
      dispatch(getLiabilitiesAction({ applicationId, silent: true })),
    ]).catch(err => {
      logger.error(err);
    });
  }, [dispatch, applicationId]);

  // Derive the field tree from the live store inputs rather than an effect, so a
  // rebuild always reflects the latest definitions/results regardless of which
  // async load (the doc's embedded ExtractionFields vs the filled results vs the
  // inspector-loaded type definitions) settles last. The loadedDocumentId guard
  // keeps the PII protection: only show fields once the results for THIS document
  // have loaded. Falls back to inspector-loaded type definitions when the document
  // was loaded without embedded ExtractionFields (most doc types via getDocumentsV2).
  const fieldTree = useMemo(() => {
    if (loadedDocumentId !== documentId) {
      return [];
    }

    const resolvedExtractionFields = !_.isEmpty(extractionFields)
      ? extractionFields
      : buildNestedExtractionFields(inspectorFlatFields ?? []);

    return getExtractionFieldsTree({
      extractionFields: resolvedExtractionFields,
      extractionFieldsResults,
    });
  }, [
    documentId,
    loadedDocumentId,
    extractionFields,
    extractionFieldsResults,
    inspectorFlatFields,
  ]);

  const hasLowConfidenceField = useMemo(
    () => fieldTree.some(field => checkIsLowConfidence(field.confidence, field.threshold)),
    [fieldTree],
  );

  // The rendered order: field-definition order by default, low-confidence-first
  // when the reviewer toggles it on, so below-threshold fields are triaged first.
  const displayedFields = useMemo(
    () => (isLowConfidenceFirst ? sortFieldsLowConfidenceFirst(fieldTree) : fieldTree),
    [fieldTree, isLowConfidenceFirst],
  );

  const virtualizedRows = useMemo(() => flattenFieldTreeRows(displayedFields), [displayedFields]);

  const rowHeightCache = useMemo(
    () =>
      new CellMeasurerCache({
        defaultHeight: VIRTUALIZED_ROW_DEFAULT_HEIGHT,
        fixedWidth: true,
      }),
    [],
  );

  useEffect(() => {
    rowHeightCache.clearAll();
    virtualizedListRef.current?.recomputeRowHeights();
  }, [rowHeightCache, virtualizedRows]);

  useEffect(() => {
    setScrollElement(findScrollParent(virtualizedListWrapperRef.current?.parentElement ?? null));
  }, [virtualizedRows.length]);

  // The flat set of this document type's field DEFINITIONS (embedded on the document when present,
  // else the inspector's fallback fetch) — the same resolution `definitionLeaves`/`extractionFieldTypeMap`
  // use, needed here to resolve each field's `name` for the locate join below.
  const flatFieldDefinitions = useMemo(
    () =>
      !_.isEmpty(extractionFields)
        ? flattenExtractionFieldDefinitions(extractionFields)
        : (inspectorFlatFields ?? []),
    [extractionFields, inspectorFlatFields],
  );

  // The extraction-results API carries no page number, so the "show in document" locate control
  // resolves each field's page from the AI-layer boxes: the mapped layer keys a box by `schema.name`,
  // which holds the field's human-readable label (e.g. "Document Title") — NOT the field definition's
  // machine-cased `aiExtractionFieldName` (e.g. "documentTitle"). Match on the definition's `name`
  // instead. Map documentExtractionFieldId -> pageNumber so a row can point the viewer at its page.
  // Fields with no mapped box get no entry (locate control stays hidden).
  const pageNumberByFieldId = useMemo(() => {
    const mappedPages = aiModelsByLayer?.[AiDataRecognitionResult.MAPPED_AI_MODEL_FIELDS]?.pages;

    if (_.isEmpty(mappedPages) || _.isEmpty(flatFieldDefinitions)) {
      return {};
    }

    const pageByAiFieldName = mappedPages.reduce((acc, page) => {
      page.fields.forEach(({ schema }) => {
        const aiFieldName = schema?.name;

        if (!_.isNil(aiFieldName) && _.isNil(acc[aiFieldName])) {
          acc[aiFieldName] = page.number;
        }
      });

      return acc;
    }, {});

    return flatFieldDefinitions.reduce((acc, { id, name }) => {
      const pageNumber = pageByAiFieldName[name];

      if (!_.isNil(id) && !_.isNil(pageNumber)) {
        acc[id] = pageNumber;
      }

      return acc;
    }, {});
  }, [aiModelsByLayer, flatFieldDefinitions]);

  // Every definition leaf of the document type — the fields extraction found no
  // value for render as explicit empty states instead of silently disappearing.
  const definitionLeaves = useMemo(
    () => getExtractionFieldLeaves({ extractionFields, inspectorFlatFields }),
    [extractionFields, inspectorFlatFields],
  );

  const missingFieldLeaves = useMemo(() => {
    if (loadedDocumentId !== documentId) {
      return [];
    }

    return getMissingExtractionFieldLeaves({ leaves: definitionLeaves, extractionFieldsResults });
  }, [documentId, loadedDocumentId, definitionLeaves, extractionFieldsResults]);

  const extractionFieldTypeMap = useMemo(
    () => buildExtractionFieldTypeMap({ extractionFields, inspectorFlatFields }),
    [extractionFields, inspectorFlatFields],
  );

  // Options for SingleSelectionDropDown definitions, keyed by field id — lets the edit form
  // prefill resolve an extracted option NAME back to the option's id (see
  // buildExtractionResultsInitialForm).
  const dropdownOptionsById = useMemo(
    () => buildDropdownOptionsMap({ extractionFields, inspectorFlatFields }),
    [extractionFields, inspectorFlatFields],
  );

  // Prefill the edit form with the document's existing filled values so "Edit all" opens with
  // the current data. The override endpoint replaces the whole result set — without prefill,
  // untouched fields (including required ones like ein) would be dropped and the save would 400.
  const editInitialForm = useMemo(() => {
    if (loadedDocumentId !== documentId) {
      return EMPTY_EXTRACTION_INITIAL_FORM;
    }

    return buildExtractionResultsInitialForm({
      extractionFieldsResults,
      fieldTypeById: extractionFieldTypeMap,
      dropdownOptionsById,
    });
  }, [
    documentId,
    loadedDocumentId,
    extractionFieldsResults,
    extractionFieldTypeMap,
    dropdownOptionsById,
  ]);

  const fieldApplyMap = useMemo(() => {
    const mappingEntry = documentExtractionMappingList?.[documentTypeId];

    if (!mappingEntry?.fieldsMapping) {
      return {};
    }

    return buildFieldApplyMap({
      fieldsMapping: mappingEntry.fieldsMapping,
      documentAssociations: documentAssociationIds,
      associationOptions: documentAssociationsOptions,
    });
  }, [
    documentExtractionMappingList,
    documentTypeId,
    documentAssociationIds,
    documentAssociationsOptions,
  ]);

  const onToggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev);
  }, []);

  const onToggleLowConfidenceFirst = useCallback(() => {
    setIsLowConfidenceFirst(prev => !prev);
  }, []);

  const onEditSaveClick = useCallback(() => {
    editFormRef.current?.submit();
  }, []);

  const onShowFieldSourceForField = useCallback(
    fieldInfo => {
      onShowFieldSource?.(fieldInfo);
    },
    [onShowFieldSource],
  );

  const onApplyToEntity = useCallback(
    ({ documentFieldTypeId: appliedFieldTypeId, fieldValue }) => {
      const applyEntry = fieldApplyMap[appliedFieldTypeId];

      if (_.isNil(applyEntry) || _.isNil(applicationId)) {
        return;
      }

      const { linkedEntityId, linkedEntityFieldLabel, entityType, linkedEntityBorrowerId } =
        applyEntry;
      const fieldKey = linkedEntityFieldLabel;

      const applyFnByEntityType = {
        assetIds: () =>
          applicationAssetApi.editAsset({
            applicationId,
            assetId: linkedEntityId,
            [fieldKey]: fieldValue,
          }),
        liabilityIds: () =>
          applicationLiabilityApi.editLiability({
            applicationId,
            liabilityId: linkedEntityId,
            [fieldKey]: fieldValue,
          }),
        propertyIds: () =>
          applicationPropertyApi.editProperty({
            applicationId,
            propertyId: linkedEntityId,
            [fieldKey]: fieldValue,
          }),
        incomeIds: () =>
          applicationIncomeApi.editIncome({
            applicationId,
            borrowerId: linkedEntityBorrowerId,
            incomeId: linkedEntityId,
            [fieldKey]: fieldValue,
          }),
        borrowerIds: () =>
          applicationBorrowerApi.editBorrowerPersonalInfo({
            applicationId,
            borrowerId: linkedEntityId,
            [fieldKey]: fieldValue,
          }),
      };

      const applyFn = applyFnByEntityType[entityType];

      if (_.isNil(applyFn)) {
        return;
      }

      applyFn()
        .then(() => {
          dispatch(
            showMessageAction({ message: 'Value applied to linked entity', variant: 'success' }),
          );
        })
        .catch(err => {
          logger.error(err);
          dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
        });
    },
    [applicationId, dispatch, fieldApplyMap],
  );

  const onRerunClick = useCallback(() => {
    setIsRerunning(true);

    dispatch(rerunDocumentExtractionAction({ documentId }))
      .then(() => {
        dispatch(showMessageAction({ message: 'Re-extraction started', variant: 'success' }));
        refreshExtractionRequestAndResults();
      })
      .catch(err => {
        logger.error(err);
        dispatch(
          showErrorMessageAction({
            message: getDefaultErrorMessage(err),
            err,
          }),
        );
      })
      .finally(() => {
        setIsRerunning(false);
      });
  }, [dispatch, documentId, refreshExtractionRequestAndResults]);

  const renderFieldCard = useCallback(
    field => {
      const hasConfidence = !_.isNil(field.confidence);
      const low = checkIsLowConfidence(field.confidence, field.threshold);
      // The apply map is keyed by documentFieldTypeId (the mapping's field-type id), while
      // each tree node's `id` is the documentExtractionField id — look up by the former.
      const applyEntry = fieldApplyMap[field.documentFieldTypeId];

      return (
        <Box sx={low ? aiFieldCardNeedsReviewStyles : aiFieldCardStyles}>
          {hasConfidence && (
            <Box sx={confidenceBadgeRowStyles}>
              <Box sx={low ? confidenceBadgeLowStyles : confidenceBadgeHighStyles}>
                <Typography variant="regular12">
                  {getConfidencePillText({
                    confidence: field.confidence,
                    threshold: field.threshold,
                  })}
                </Typography>
              </Box>
            </Box>
          )}
          <ViewExtractionField
            {...field}
            linkedEntityFieldLabel={applyEntry?.linkedEntityFieldLabel ?? null}
            linkedEntityLabel={applyEntry?.linkedEntityLabel ?? null}
            pageNumber={pageNumberByFieldId[field.id] ?? null}
            onApplyToEntity={applyEntry ? onApplyToEntity : null}
            onEditField={isManageAllowed ? onToggleEditMode : null}
            onShowFieldSource={!_.isNil(onShowFieldSource) ? onShowFieldSourceForField : null}
          />
          {low && isManageAllowed && (
            <Box sx={reviewActionsRowStyles}>
              <Box
                component="button"
                data-testid={DocumentExtractionFieldsTabTestIds.CORRECT_BUTTON}
                sx={correctButtonStyles}
                type="button"
                onClick={onToggleEditMode}
              >
                <EditPenIcon size={12} />
                Correct
              </Box>
            </Box>
          )}
        </Box>
      );
    },
    [
      fieldApplyMap,
      isManageAllowed,
      onApplyToEntity,
      onShowFieldSource,
      onShowFieldSourceForField,
      onToggleEditMode,
      pageNumberByFieldId,
    ],
  );

  const rowRenderer = useCallback(
    ({ index, parent, style }) => {
      const row = virtualizedRows[index];
      const isNested = !_.isEmpty(row.frames);
      const innermostFrame = _.last(row.frames);

      const framedField = row.frames.reduceRight(
        (content, frame) => (
          <Box key={frame.key} sx={getFrameSegmentStyles(frame.kind, frame.isFirst, frame.isLast)}>
            {frame.isFirst && !_.isNil(frame.name) && (
              <Typography color="text.secondary" sx={frameLabelStyles} variant="regular12">
                {frame.name}
              </Typography>
            )}
            {content}
          </Box>
        ),
        isNested ? <ViewExtractionField {...row.field} /> : renderFieldCard(row.field),
      );

      return (
        <CellMeasurer
          cache={rowHeightCache}
          columnIndex={0}
          key={row.key}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <Box
              ref={registerChild}
              style={style}
              sx={getVirtualizedRowStyles(isNested, innermostFrame?.isFirst ?? false)}
            >
              {framedField}
            </Box>
          )}
        </CellMeasurer>
      );
    },
    [renderFieldCard, rowHeightCache, virtualizedRows],
  );

  if (isEditMode) {
    return (
      <>
        <Box sx={editFormWrapperStyles}>
          <Box sx={sectionHeaderStyles}>
            <Typography color="text.secondary" variant="bold12">
              {EXTRACTION_SECTION_LABEL.EDIT}
            </Typography>
          </Box>
          <EditExtractionFieldsResultsForm
            hideSaveButton
            documentExtractionResultsInitialForm={editInitialForm}
            documentId={documentId}
            documentTypeId={documentTypeId}
            getExtractionResults={refreshExtractionRequestAndResults}
            isLoading={isSubmitting}
            ref={editFormRef}
            setIsLoading={setIsSubmitting}
          />
        </Box>
        <Box sx={footerStyles}>
          <Button
            fullWidth
            data-testid={DocumentExtractionFieldsTabTestIds.EDIT_CANCEL_BUTTON}
            disabled={isSubmitting}
            variant="outlined"
            onClick={onToggleEditMode}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            data-testid={DocumentExtractionFieldsTabTestIds.EDIT_SAVE_BUTTON}
            disabled={isSubmitting}
            variant="contained"
            onClick={onEditSaveClick}
          >
            Save
          </Button>
        </Box>
      </>
    );
  }

  const isProcessing = panelStatus === EXTRACTION_PANEL_STATUS.PROCESSING;
  const isFailed = panelStatus === EXTRACTION_PANEL_STATUS.FAILED;
  const isAwaitingManual = panelStatus === EXTRACTION_PANEL_STATUS.AWAITING_MANUAL;
  const isResultsLoaded = loadedDocumentId === documentId;
  const hasResults = fieldTree.length > 0;
  const hasDefinitions = definitionLeaves.length > 0;
  const areEmptyFieldCardsVisible = !isProcessing && !isError && !hasResults && isResultsLoaded;
  const isEditActionAllowed = isManageAllowed && hasDefinitions && !isProcessing && !isError;
  const sectionLabel = isAwaitingManual
    ? EXTRACTION_SECTION_LABEL.MANUAL_ENTRY
    : EXTRACTION_SECTION_LABEL.EXTRACTED;

  return (
    <>
      <Box>
        <Box sx={sectionHeaderStyles}>
          <Typography color="text.secondary" variant="bold12">
            {sectionLabel}
          </Typography>
          {hasResults && (
            <Box sx={headerActionsStyles}>
              {hasLowConfidenceField && (
                <Box
                  aria-pressed={isLowConfidenceFirst}
                  component="button"
                  data-testid={DocumentExtractionFieldsTabTestIds.SORT_LOW_CONFIDENCE_TOGGLE}
                  sx={getSortToggleButtonStyles({ isActive: isLowConfidenceFirst })}
                  type="button"
                  onClick={onToggleLowConfidenceFirst}
                >
                  {SORT_LOW_CONFIDENCE_FIRST_LABEL}
                </Box>
              )}
              {isManageAllowed && (
                <Box
                  component="button"
                  data-testid={DocumentExtractionFieldsTabTestIds.EDIT_ALL_LINK}
                  sx={editAllButtonStyles}
                  type="button"
                  onClick={onToggleEditMode}
                >
                  <EditPenIcon size={13} />
                  Edit all
                </Box>
              )}
            </Box>
          )}
        </Box>

        {isProcessing &&
          SKELETON_CARD_KEYS.map(key => (
            <Box key={key} sx={skeletonCardStyles}>
              <Skeleton height={12} width="40%" />
              <Skeleton height={16} width="85%" />
            </Box>
          ))}

        {!isProcessing && isError && (
          <Box sx={emptyFieldsBoxStyles}>
            <Typography color="text.secondary" variant="regular14">
              Could not load AI fields for this document
            </Typography>
          </Box>
        )}

        {areEmptyFieldCardsVisible && !hasDefinitions && (
          <Box sx={emptyFieldsBoxStyles}>
            <Typography color="text.secondary" variant="regular14">
              No AI fields extracted for this document
            </Typography>
          </Box>
        )}

        {areEmptyFieldCardsVisible &&
          definitionLeaves.map(({ id, name }) => (
            <Box key={id} sx={aiFieldCardStyles}>
              <Typography color="text.secondary" variant="regular12">
                {name}
              </Typography>
              <Typography color="text.disabled" sx={emptyFieldValueStyles} variant="regular14">
                —
              </Typography>
            </Box>
          ))}

        {!isProcessing && hasResults && (
          <Box ref={virtualizedListWrapperRef} sx={virtualizedListStyles}>
            <WindowScroller scrollElement={scrollElement ?? undefined}>
              {({ height, isScrolling, onChildScroll, scrollTop, registerChild }) => (
                <AutoSizer disableHeight>
                  {({ width }) => (
                    <div ref={registerChild}>
                      <List
                        autoHeight
                        deferredMeasurementCache={rowHeightCache}
                        height={height}
                        isScrolling={isScrolling}
                        overscanRowCount={VIRTUALIZED_OVERSCAN_ROW_COUNT}
                        ref={virtualizedListRef}
                        rowCount={virtualizedRows.length}
                        rowHeight={rowHeightCache.rowHeight}
                        rowRenderer={rowRenderer}
                        scrollTop={scrollTop}
                        width={width}
                        onScroll={onChildScroll}
                      />
                    </div>
                  )}
                </AutoSizer>
              )}
            </WindowScroller>
          </Box>
        )}

        {!isProcessing && hasResults && missingFieldLeaves.length > 0 && (
          <Box data-testid={DocumentExtractionFieldsTabTestIds.NOT_FOUND_ACCORDION}>
            <AccordionCDS
              arrowSize={16}
              className=""
              customStyles={notFoundAccordionStyles}
              title={`Not found on document (${missingFieldLeaves.length})`}
              titleVariant="medium12"
            >
              {missingFieldLeaves.map(({ id, name }) => (
                <Box key={id} sx={miniFieldRowStyles}>
                  <Typography color="text.secondary" variant="regular12">
                    {name}
                  </Typography>
                  <Typography color="text.disabled" variant="regular12">
                    —
                  </Typography>
                </Box>
              ))}
            </AccordionCDS>
          </Box>
        )}
      </Box>

      <Box sx={footerStyles}>
        {isFailed ? (
          <Button
            fullWidth
            data-testid={DocumentExtractionFieldsTabTestIds.RERUN_BUTTON}
            disabled={isRerunning}
            sx={footerButtonStyles}
            variant="contained"
            onClick={onRerunClick}
          >
            {RERUN_BUTTON_LABEL.RETRY}
          </Button>
        ) : (
          <Tooltip title={isProcessing ? RERUN_DISABLED_PROCESSING_TOOLTIP : ''}>
            <Box sx={footerButtonWrapStyles}>
              <Button
                fullWidth
                data-testid={DocumentExtractionFieldsTabTestIds.RERUN_BUTTON}
                disabled={isRerunning || isProcessing}
                sx={footerButtonStyles}
                variant="outlined"
                onClick={onRerunClick}
              >
                {RERUN_BUTTON_LABEL.RERUN}
              </Button>
            </Box>
          </Tooltip>
        )}
        {isEditActionAllowed && !isFailed && (
          <Box sx={footerButtonWrapStyles}>
            <Button
              fullWidth
              data-testid={DocumentExtractionFieldsTabTestIds.EDIT_ALL_BUTTON}
              disabled={isSubmitting}
              sx={footerButtonStyles}
              variant="contained"
              onClick={onToggleEditMode}
            >
              {hasResults ? EDIT_BUTTON_LABEL.EDIT_ALL : EDIT_BUTTON_LABEL.ENTER_VALUES}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

DocumentExtractionFieldsTab.propTypes = {
  documentId: PropTypes.string.isRequired,
  documentTypeId: PropTypes.string,
  applicationId: PropTypes.string,
  panelStatus: PropTypes.string,
  onRefreshExtractionRequest: PropTypes.func,
  onShowFieldSource: PropTypes.func,
};

export default memo(DocumentExtractionFieldsTab);
